import React from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  Chip,
  Divider,
  Link,
  Alert,
  TextField,
  CircularProgress,
  Tooltip,
  Collapse,
  InputAdornment,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HistoryIcon from '@mui/icons-material/History';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import moment from 'moment';

import { StateManager } from '../utilities/stateManager';
import RequestManager, { getStipDocumentUrl } from '../utilities/requestManager';
import constants from '../utilities/constants';
import store from '../utilities/store';
import { StackedDialog } from '../utilities/ModalStackContext';

const parseEmails = (str) => (str || '').split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

const requirementsFor = (lenderId) =>
  constants.lenderStipRequirements[lenderId] || constants.defaultStipRequirements;

// A document the server covered the NTO on. Its Storage copy still shows the
// amount, so it must be viewed through previewStipDocument instead.
const redacted = (doc) => ['field', 'text', 'near'].includes(doc.ntoRedaction);

// Mirrors isConsignmentFile in the backend's stips.js: a file bank pick whose name
// or tags say consignment gets the NTO blacked out on the way out.
const isConsignmentFile = (file) =>
  /consign/.test([file.name, ...(file.tags || [])].filter(Boolean).join(' ').toLowerCase());

// Pick lenders → see which stips are already on the car → send the ones you choose.
// Documents are resolved server-side on open so nothing has to be hunted down in
// another tab; the same resolver runs again on send, so it stays authoritative.
export default function SendStipsDialog({ open, onClose, stockNumber, onSent }) {
  const [loading, setLoading] = React.useState(true);
  const [preview, setPreview] = React.useState(null);
  const [selectedBanks, setSelectedBanks] = React.useState([]);
  const [selectedDocs, setSelectedDocs] = React.useState([]);
  const [touchedDocs, setTouchedDocs] = React.useState(false);
  const [note, setNote] = React.useState('');
  const [extraCc, setExtraCc] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [history, setHistory] = React.useState([]);
  const [showHistory, setShowHistory] = React.useState(false);
  // The file bank stays shut until someone needs something off the standard list.
  const [filesOpen, setFilesOpen] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState([]);
  const [fileSearch, setFileSearch] = React.useState('');

  const lenders = StateManager.banks;

  // Resolve the car's documents once per open. Lender requirements come from
  // constants, so toggling a lender costs no round-trip.
  React.useEffect(() => {
    if (!open || !stockNumber) return;
    let active = true;
    setLoading(true);
    setPreview(null);
    setSelectedBanks([]);
    setSelectedDocs([]);
    setTouchedDocs(false);
    setNote('');
    setExtraCc('');
    setShowHistory(false);
    setFilesOpen(false);
    setSelectedFiles([]);
    setFileSearch('');

    RequestManager.post({ function: 'previewBankStips', variables: { stockNumber } })
      .then(res => {
        if (!active) return;
        if (res.error) throw new Error(res.error);
        setPreview(res);
      })
      .catch(e => {
        if (!active) return;
        StateManager.setAlertAndOpen(e.message || 'Could not load documents for this car', 'error');
      })
      .finally(() => { if (active) setLoading(false); });

    store.getStipSends({ stockNumber })
      .then(rows => { if (active) setHistory(rows); })
      .catch(() => { if (active) setHistory([]); });

    return () => { active = false; };
  }, [open, stockNumber]);

  // Memoized so the derived map and the auto-select effect below keep a stable
  // identity; a fresh `[]` each render would re-fire the effect forever.
  const documents = React.useMemo(() => preview?.documents || [], [preview]);
  const docByKey = React.useMemo(() => {
    const map = {};
    documents.forEach(d => { map[d.key] = d; });
    return map;
  }, [documents]);

  // What the chosen lenders collectively ask for, and which of those we hold.
  const requiredKeys = React.useMemo(() => {
    const keys = new Set();
    selectedBanks.forEach(id => requirementsFor(id).forEach(k => keys.add(k)));
    return [...keys];
  }, [selectedBanks]);

  const missingKeys = requiredKeys.filter(k => !docByKey[k]?.available);

  // Default the selection to every required document we actually have. Once the
  // user edits the list, leave it alone. Keeps the previous array when the
  // contents match so an unstable dep identity can never loop this.
  React.useEffect(() => {
    if (touchedDocs) return;
    const next = requiredKeys.filter(k => docByKey[k]?.available);
    setSelectedDocs(prev =>
      (prev.length === next.length && next.every(k => prev.includes(k))) ? prev : next);
  }, [requiredKeys, docByKey, touchedDocs]);

  const toggleBank = (id) => setSelectedBanks(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleDoc = (key) => {
    setTouchedDocs(true);
    setSelectedDocs(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  // The car's other document-library files. Files already standing in for a stip
  // are listed but locked, so it is obvious they are going out either way.
  const files = React.useMemo(() => preview?.files || [], [preview]);
  const visibleFiles = React.useMemo(() => {
    const q = fileSearch.trim().toLowerCase();
    if (!q) return files;
    return files.filter(f =>
      [f.name, f.category, ...(f.tags || [])].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [files, fileSearch]);

  const toggleFile = (docId) => setSelectedFiles(prev =>
    prev.includes(docId) ? prev.filter(x => x !== docId) : [...prev, docId]);

  // Which lenders want a given document — shown per row so staff understand why.
  const wantedBy = (key) => lenders
    .filter(l => selectedBanks.includes(l.data.id) && requirementsFor(l.data.id).includes(key))
    .map(l => l.label);

  const resend = (record) => {
    setTouchedDocs(true);
    setSelectedBanks((record.banks || []).map(b => b.id).filter(Boolean));
    setSelectedDocs((record.documents || []).map(d => d.key).filter(k => docByKey[k]?.available));

    // Restore the extra files too, but only ones still linked to the car.
    const stillLinked = (record.extraFileIds || []).filter(id => files.some(f => f.docId === id));
    setSelectedFiles(stillLinked);
    if (stillLinked.length) setFilesOpen(true);

    setNote(record.note || '');
    setShowHistory(false);
    StateManager.setAlertAndOpen('Loaded that send — review and send again.', 'info');
  };

  const send = async () => {
    setSending(true);
    try {
      const res = await RequestManager.post({
        function: 'sendBankStips',
        variables: {
          stockNumber,
          banks: selectedBanks,
          documents: selectedDocs,
          extraFileIds: selectedFiles,
          sentById: StateManager.userID,
          extraCc: parseEmails(extraCc),
          note: note.trim() || null,
        },
      });
      if (res.success === false || res.error) throw new Error(res.error || 'Send failed');

      const linked = (res.linked || []).length;
      StateManager.setAlertAndOpen(
        `Stips sent to ${(res.banks || []).length} lender(s)${linked ? ` — ${linked} document(s) delivered as links` : ''}`,
        'success',
      );
      // One warning, not two — a second setAlertAndOpen replaces the first.
      const problems = [
        ...(res.blocked || []).map(b => `${b.label} was not sent — ${b.reason}`),
        ...((res.failed || []).length
          ? [`Could not retrieve: ${res.failed.map(k => docByKey[k]?.label || k).join(', ')}`]
          : []),
      ];
      if (problems.length) StateManager.setAlertAndOpen(problems.join(' '), 'warning');
      if (onSent) onSent(res);
      onClose();
    } catch (e) {
      StateManager.setAlertAndOpen(e.message || 'Error sending stipulations', 'error');
    } finally {
      setSending(false);
    }
  };

  const totalSelected = selectedDocs.length + selectedFiles.length;
  const canSend = selectedBanks.length > 0 && totalSelected > 0 && !sending;

  return (
    <StackedDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <span>Send Stips to Bank</span>
        {preview?.carLabel && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {preview.carLabel} · Stock #{preview.stockNumber}
            {preview.buyerName ? ` · ${preview.buyerName}` : ''}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {history.length > 0 && (
          <Button
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistory(v => !v)}
          >
            {showHistory ? 'Hide history' : `History (${history.length})`}
          </Button>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        )}

        {!loading && showHistory && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Previous sends</Typography>
            {history.map(record => (
              <Box
                key={record.id}
                sx={{
                  border: 1, borderColor: 'divider', borderRadius: 1.5,
                  p: 1.5, mb: 1, display: 'flex', gap: 1.5, alignItems: 'flex-start',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {record.sentAt?.seconds
                      ? moment.unix(record.sentAt.seconds).format('MMM D, YYYY h:mm A')
                      : 'Date unknown'}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    To {(record.banks || []).map(b => b.name).join(', ') || '—'}
                    {record.sentByEmail ? ` · by ${constants.userNames?.[record.sentById] || record.sentByEmail}` : ''}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                    {(record.documents || []).map(d => (
                      <Chip
                        key={d.key}
                        size="small"
                        label={d.delivered === 'link' ? `${d.label} (link)` : d.label}
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    ))}
                  </Box>
                </Box>
                <Button size="small" startIcon={<ReplayIcon />} onClick={() => resend(record)}>
                  Resend
                </Button>
              </Box>
            ))}
          </Box>
        )}

        {!loading && preview && (
          <Box>
            {/* Lenders */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Lenders</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              Choosing a lender pre-selects the documents it asks for.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {lenders.map(lender => {
                const id = lender.data.id;
                const hasEmail = !!lender.data.email;
                const checked = selectedBanks.includes(id);
                const req = requirementsFor(id);
                const gaps = req.filter(k => !docByKey[k]?.available).length;
                return (
                  <Tooltip
                    key={lender.value}
                    title={hasEmail
                      ? `${lender.data.email} · wants ${req.length} document(s)`
                      : 'No email configured — set one in Lender Management'}
                  >
                    <Box
                      onClick={() => hasEmail && toggleBank(id)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5,
                        border: 1, borderRadius: 1.5, px: 1, py: 0.5,
                        borderColor: checked ? 'primary.main' : 'divider',
                        bgcolor: (t) => checked ? alpha(t.palette.primary.main, 0.07) : 'transparent',
                        cursor: hasEmail ? 'pointer' : 'not-allowed',
                        opacity: hasEmail ? 1 : 0.5,
                      }}
                    >
                      <Checkbox size="small" checked={checked} disabled={!hasEmail} sx={{ p: 0.25 }} />
                      <Typography variant="body2" sx={{ fontWeight: checked ? 600 : 400 }}>
                        {lender.label}
                      </Typography>
                      {checked && gaps > 0 && (
                        <Chip size="small" color="warning" label={`${gaps} missing`} sx={{ fontSize: '0.6rem', height: 17 }} />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Documents on the car */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Documents on this car</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
              Open any document to check it before sending. Pick exactly what goes out.
            </Typography>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', mb: 2 }}>
              {documents.map((doc, i) => {
                const required = requiredKeys.includes(doc.key);
                const wants = wantedBy(doc.key);
                return (
                  <Box
                    key={doc.key}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      px: 1, py: 1,
                      borderBottom: i < documents.length - 1 ? 1 : 0, borderColor: 'divider',
                      bgcolor: (t) => required && !doc.available
                        ? alpha(t.palette.warning.main, 0.08)
                        : (i % 2 === 1 ? 'grey.50' : 'transparent'),
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedDocs.includes(doc.key)}
                      onChange={() => toggleDoc(doc.key)}
                      disabled={!doc.available}
                    />
                    {doc.available
                      ? (doc.generated
                          ? <AutoAwesomeIcon sx={{ fontSize: 18, color: 'info.dark' }} />
                          : <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />)
                      : <ErrorOutlineIcon sx={{ fontSize: 18, color: 'warning.dark' }} />}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.label}</Typography>
                        {required && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={doc.available ? 'default' : 'warning'}
                            label={`Required by ${wants.join(', ')}`}
                            sx={{ fontSize: '0.6rem', height: 17 }}
                          />
                        )}
                        {doc.legacy && (
                          <Chip size="small" variant="outlined" label="from file library" sx={{ fontSize: '0.6rem', height: 17 }} />
                        )}
                        {/* The lender never sees the NTO. `View` opens the redacted
                            copy, so the chip can be checked instead of trusted. */}
                        {['field', 'text', 'near'].includes(doc.ntoRedaction) && (
                          <Tooltip title="View opens the redacted copy — exactly what the lender receives, with the NTO amount covered.">
                            <Chip
                              size="small"
                              variant="outlined"
                              color="info"
                              label="NTO blacked out"
                              sx={{ fontSize: '0.6rem', height: 17 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ color: doc.available ? 'text.secondary' : 'warning.dark', display: 'block' }}
                      >
                        {doc.source}
                      </Typography>
                    </Box>

                    {/* A redacted document is viewed through the send path, not
                        through Storage — the Storage copy still shows the NTO. */}
                    {(doc.url || redacted(doc)) && (
                      <Link
                        href={redacted(doc)
                          ? getStipDocumentUrl(stockNumber, { key: doc.key })
                          : doc.url}
                        target="_blank"
                        rel="noopener"
                        variant="caption"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, mr: 1 }}
                      >
                        View <OpenInNewIcon sx={{ fontSize: 13 }} />
                      </Link>
                    )}
                  </Box>
                );
              })}
            </Box>

            {missingKeys.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Not on file yet: {missingKeys.map(k => docByKey[k]?.label || k).join(', ')}. You can still
                send what you have and follow up with the rest.
              </Alert>
            )}

            {/* File bank — collapsed by default; opened when a lender wants
                something outside the standard stip list. */}
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, mb: 2, overflow: 'hidden' }}>
              <Box
                onClick={() => setFilesOpen(v => !v)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1.5, py: 1, cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FolderOpenIcon sx={{ fontSize: 19, color: 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Other files on this car
                </Typography>
                <Chip size="small" label={files.length} sx={{ fontSize: '0.65rem', height: 18 }} />
                {selectedFiles.length > 0 && (
                  <Chip
                    size="small"
                    color="primary"
                    label={`${selectedFiles.length} selected`}
                    sx={{ fontSize: '0.65rem', height: 18 }}
                  />
                )}
                <Box sx={{ flex: 1 }} />
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20, color: 'text.secondary',
                    transform: filesOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }}
                />
              </Box>

              <Collapse in={filesOpen} unmountOnExit>
                <Divider />
                <Box sx={{ p: 1.5 }}>
                  {files.length === 0 ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      No other files are linked to this car. Upload them on the car's Files tab.
                    </Typography>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name, category, or tag…"
                        value={fileSearch}
                        onChange={e => setFileSearch(e.target.value)}
                        sx={{ mb: 1 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {visibleFiles.length === 0 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          No files match “{fileSearch}”.
                        </Typography>
                      )}

                      <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                        {visibleFiles.map((file, i) => {
                          const covered = !!file.usedAs;
                          return (
                            <Box
                              key={file.docId}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 0.5, py: 0.75,
                                borderBottom: i < visibleFiles.length - 1 ? 1 : 0,
                                borderColor: 'divider',
                                opacity: covered ? 0.6 : 1,
                              }}
                            >
                              <Tooltip title={covered ? `Already going out as “${file.usedAs}”` : ''}>
                                <span>
                                  <Checkbox
                                    size="small"
                                    checked={covered || selectedFiles.includes(file.docId)}
                                    onChange={() => toggleFile(file.docId)}
                                    disabled={covered}
                                  />
                                </span>
                              </Tooltip>

                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                  {file.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    label={file.category}
                                    sx={{ fontSize: '0.6rem', height: 16, textTransform: 'capitalize' }}
                                  />
                                  {file.date && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {file.date}
                                    </Typography>
                                  )}
                                  {covered && (
                                    <Typography variant="caption" sx={{ color: 'success.dark' }}>
                                      already included as {file.usedAs}
                                    </Typography>
                                  )}
                                  {isConsignmentFile(file) && (
                                    <Chip
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      label="NTO blacked out"
                                      sx={{ fontSize: '0.6rem', height: 16 }}
                                    />
                                  )}
                                </Box>
                              </Box>

                              {/* Same rule in the file bank: a consignment agreement
                                  picked here is redacted on the way out, so view the
                                  redacted copy rather than the Storage original. */}
                              {(file.url || isConsignmentFile(file)) && (
                                <Link
                                  href={isConsignmentFile(file)
                                    ? getStipDocumentUrl(stockNumber, { fileId: file.docId })
                                    : file.url}
                                  target="_blank"
                                  rel="noopener"
                                  variant="caption"
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, mr: 0.5 }}
                                >
                                  View <OpenInNewIcon sx={{ fontSize: 13 }} />
                                </Link>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Box>
              </Collapse>
            </Box>

            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Note to the lender (optional)"
              value={note}
              onChange={e => setNote(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Additional CC (optional)"
              placeholder="email@example.com, another@example.com"
              value={extraCc}
              onChange={e => setExtraCc(e.target.value)}
              helperText="Comma-separated. Natalie and the deal's sales rep are always CC'd."
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          onClick={send}
          disabled={!canSend}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {sending
            ? 'Sending…'
            : `Send ${totalSelected} doc${totalSelected !== 1 ? 's' : ''} to ${selectedBanks.length} lender${selectedBanks.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
